import {
  Box,
  Button,
  Grid,
  HStack,
  IconButton,
  Skeleton,
  Stack,
  Text,
  useClipboard,
  useToast,
} from "@chakra-ui/react";
import FileSaver from "file-saver";
import React from "react";
import { MaterialIcon } from "../../../components/shared/MaterialIcon";
import { usePostFetchIndividualFile } from "../../../services/api/data/mutations/usePostFetchIndividualFile";
import { useReadRegistryIndividual } from "../../../services/api/data/queries/useReadRegistryIndividual";

interface IProps {
  registryId: string;
  individualId: string;
}

export const IndividualContainer = ({ registryId, individualId }: IProps) => {
  const [downloadFileField, setDownloadFileField] = React.useState<string>("");

  const { data: dataIndividual, isLoading: isLoadingIndividual } =
    useReadRegistryIndividual({
      registryId,
      individualId: individualId,
    });

  const { mutateAsync: mutateAsyncPostGetFile } = usePostFetchIndividualFile();

  const toast = useToast();

  const { onCopy: onCopyIndividualEmail, value: valueEmail } = useClipboard(
    dataIndividual?.email || ""
  );
  const { onCopy: onCopyGuardianEmail, value: valueGuardianEmail } =
    useClipboard(dataIndividual?.contact_email || "");

  const handleOnDownloadFile = async (fileName, fileField) => {
    setDownloadFileField(fileField);

    let contentType = "image/*";

    const fileExt = fileName.split(".").pop();

    if (fileExt === "pdf") {
      contentType = "application/pdf";
    } else if (fileExt === "png") {
      contentType = "image/png";
    } else if (fileExt === "jpg") {
      contentType = "image/jpg";
    }

    try {
      const signedUrl = await mutateAsyncPostGetFile({
        content_type: contentType,
        file_name: fileName,
        registry_id: registryId,
        individual_id: individualId,
        file_field: fileField,
      });

      // Download url to local computer
      FileSaver.saveAs(signedUrl, fileName);

      toast({
        title: "Downloaded file",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (e) {
      toast({
        title: "Failed to download",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }

    setDownloadFileField("");
  };

  const handleOnCopyEmail = () => {
    onCopyIndividualEmail();

    toast({
      title: "Copied",
      description: valueEmail,
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };
  const handleOnCopyGuardianEmail = () => {
    onCopyGuardianEmail();

    toast({
      title: "Copied",
      description: valueGuardianEmail,
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  return (
    <Box w="full" h="full">
      <Grid templateColumns={{ base: "1fr", lg: "1fr 2fr" }} gap={8}>
        {isLoadingIndividual ? (
          <Stack spacing={8}>
            <Skeleton height="3.5rem" />
            <Skeleton height="2rem" />
            <Skeleton height="2rem" />
            <Skeleton height="2rem" />
          </Stack>
        ) : (
          <Stack spacing={4}>
            <Stack spacing={1}>
              <HStack>
                <MaterialIcon
                  name="account_circle"
                  color="text.softer"
                  variant="outline"
                  fontSize="2rem"
                />
                <Text textStyle={"title"} color="text.primary" fontSize="3xl">
                  {dataIndividual?.first_name} {dataIndividual?.last_name}
                </Text>
              </HStack>
            </Stack>
            <Stack spacing={1}>
              <Text textStyle={"label"}>Guardian</Text>
              <Text>{dataIndividual?.contact_name}</Text>
            </Stack>
            <Stack spacing={1}>
              <Text textStyle={"label"}>Guardian Phone</Text>
              <Text>{dataIndividual?.contact_phone}</Text>
            </Stack>
            <Stack spacing={1}>
              <Text textStyle={"label"}>Guardian Email</Text>
              <HStack spacing={4} justify="space-between">
                <Text>{dataIndividual?.contact_email}</Text>
                <IconButton
                  size="sm"
                  onClick={handleOnCopyGuardianEmail}
                  icon={
                    <MaterialIcon
                      fontSize="sm"
                      name="content_copy"
                      variant="outline"
                    />
                  }
                  aria-label="copy email"
                ></IconButton>
              </HStack>
            </Stack>
            <Stack spacing={1}>
              <Text textStyle={"label"}>Date of Birth</Text>
              <Text>{dataIndividual?.date_birth}</Text>
            </Stack>
            <Stack spacing={1}>
              <Text textStyle={"label"}>Location</Text>
              <Text>{dataIndividual?.mailing_address}</Text>
              <Text>{dataIndividual?.city}</Text>
              <Text>{dataIndividual?.state_province_territory}</Text>
              <Text>{dataIndividual?.postal_code}</Text>
              <Text>{dataIndividual?.country}</Text>
            </Stack>
            <Stack spacing={1}>
              <Text textStyle={"label"}>Sex at birth</Text>
              <Text>{dataIndividual?.sex_at_birth}</Text>
            </Stack>
            <Stack spacing={1}>
              <Text textStyle={"label"}>Email</Text>
              <HStack spacing={4} justify="space-between">
                <Text>{dataIndividual?.email}</Text>
                <IconButton
                  size="sm"
                  onClick={handleOnCopyEmail}
                  icon={
                    <MaterialIcon
                      fontSize="sm"
                      name="content_copy"
                      variant="outline"
                    />
                  }
                  aria-label="copy email"
                ></IconButton>
              </HStack>
            </Stack>
          </Stack>
        )}

        <Stack flex={1}>
          <Stack spacing={1}>
            <Text textStyle={"title"} fontSize="3xl">
              Documents
            </Text>
          </Stack>

          <Stack>
            {isLoadingIndividual ? (
              <Stack spacing={8}>
                <Skeleton height="2rem" />
                <Skeleton height="2rem" />
                <Skeleton height="2rem" />
              </Stack>
            ) : (
              <Stack spacing={8}>
                {dataIndividual?.files?.map((file_item) => (
                  <Box key={file_item.file_field}>
                    <Text textStyle={"description"}>
                      {file_item.file_field}
                    </Text>
                    <Text textStyle={"descriptionValue"}>
                      {file_item.file_name}
                    </Text>
                    <Button
                      size="sm"
                      variant="outline"
                      isLoading={
                        downloadFileField &&
                        downloadFileField === file_item.file_field
                          ? true
                          : false
                      }
                      onClick={() =>
                        handleOnDownloadFile(
                          file_item.file_name,
                          file_item.file_field
                        )
                      }
                      leftIcon={
                        <MaterialIcon name="file_download" fontSize="1rem" />
                      }
                      aria-label="download file"
                    >
                      Download
                    </Button>
                  </Box>
                ))}
              </Stack>
            )}
            {dataIndividual && !dataIndividual.files?.length && (
              <Stack>
                <Text color="text.soft">
                  No files found for {dataIndividual?.first_name}
                </Text>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Grid>
    </Box>
  );
};
